import { BellRing, Banknote, FileText, Landmark, Percent, Wallet } from "lucide-react";
import { Suspense, lazy } from "react";
import { defineFeature } from "../types";

/**
 * Payments — the money coming back in (roadmap item 4, PRD US-4.5 … US-4.7).
 *
 * `/payments` is the receipt register, `/payments/:id` is one receipt and what
 * it settles, `/receivables` is the collections cockpit and
 * `/receivables/statement` is the statement of account a client asks for. The
 * reminder cadence registers itself as a settings section, so the settings
 * shell picks it up with no coupling.
 *
 * Every page is lazy: this module costs the entry bundle its icons and its
 * metadata, nothing more.
 */

const ReminderSettingsPage = lazy(() => import("./reminders"));

/**
 * Deliberately plain markup rather than <Skeleton>: the registry imports this
 * module statically, so anything it touches lands in the entry chunk.
 */
function SettingsFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading reminder settings">
      <div className="skeleton-shimmer h-6 w-56 rounded-item" />
      <div className="skeleton-shimmer h-64 w-full rounded-card" />
      <div className="skeleton-shimmer h-72 w-full rounded-card" />
    </div>
  );
}

export default defineFeature({
  key: "payments",
  title: "Payments",
  entities: ["payment"],
  routes: [
    { path: "payments", lazy: () => import("./page") },
    { path: "payments/tds", lazy: () => import("./tds") },
    { path: "payments/:id", lazy: () => import("./detail") },
    { path: "receivables", lazy: () => import("./receivables") },
    { path: "receivables/statement", lazy: () => import("./statement") },
  ],
  nav: [
    {
      label: "Payments",
      to: "/payments",
      icon: Banknote,
      group: "finance",
      order: 20,
      permission: "invoices:read",
      description: "Receipts, TDS and what each one settled",
    },
    {
      label: "Receivables",
      to: "/receivables",
      icon: Landmark,
      group: "finance",
      order: 25,
      permission: ["invoices:read", "financials:view"],
      description: "Ageing, DSO and the reminders already sent",
    },
  ],
  quickCreate: [
    {
      key: "payments",
      label: "Payment",
      icon: Wallet,
      to: "/payments?new=1",
      permission: "payments:record",
      order: 50,
      description: "Record money received and settle invoices",
    },
  ],
  commands: [
    {
      key: "payments.unapplied",
      label: "Receipts not yet applied",
      icon: Wallet,
      to: "/payments?unappliedOnly=true",
      group: "actions",
      permission: "invoices:read",
      keywords: ["unallocated", "credit", "advance", "on account"],
      description: "Money received that no invoice has claimed",
    },
    {
      key: "payments.ageing",
      label: "Receivables ageing",
      icon: Landmark,
      to: "/receivables",
      group: "navigate",
      permission: ["invoices:read", "financials:view"],
      keywords: ["dso", "overdue", "collections", "chase", "outstanding", "buckets"],
    },
    {
      key: "payments.statement",
      label: "Client statement",
      icon: FileText,
      to: "/receivables/statement",
      group: "navigate",
      permission: "invoices:read",
      keywords: ["ledger", "soa", "account statement", "running balance"],
    },
    {
      key: "payments.tds",
      label: "TDS register",
      icon: Percent,
      to: "/payments/tds",
      group: "navigate",
      permission: "invoices:read",
      keywords: ["194j", "194c", "form 16a", "certificate", "withholding", "tax deducted"],
      description: "What clients kept back, and which certificates arrived",
    },
  ],
  settingsSections: [
    {
      key: "reminders",
      label: "Payment reminders",
      path: "reminders",
      group: "Finance",
      order: 310,
      icon: BellRing,
      permission: "invoices:read",
      description: "The cadence, the quiet hours and who is paused",
      element: (
        <Suspense fallback={<SettingsFallback />}>
          <ReminderSettingsPage />
        </Suspense>
      ),
    },
  ],
});
